import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

type DomainStatus = "idle" | "checking" | "valid" | "invalid" | "error";

const normalizeDomain = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "";

  const fromEmail = normalized.includes("@")
    ? normalized.split("@").pop() ?? normalized
    : normalized;

  return fromEmail.replace(/^@+/, "");
};

const getEffectiveDomain = (
  emailValue?: string,
  domainValue?: string,
  fallbackDomain?: string,
) => {
  const emailDomain = normalizeDomain(emailValue ?? "");
  if (emailDomain) {
    return emailDomain;
  }

  const manualDomain = normalizeDomain(domainValue ?? "");
  if (manualDomain) {
    return manualDomain;
  }

  return normalizeDomain(fallbackDomain ?? "");
};

interface LoginTenantInfo {
  name?: string;
  logoUrl?: string | null;
}

const createSchema = (labels: Required<LoginPageLabels>) => z.object({
  email: z.string().email(),
  password: z.string().min(6, { message: labels.passwordMinLength }),
  domain: z.string()
    .min(1, labels.domainRequired)
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, labels.domainRequired)
});

type LoginFormData = z.infer<ReturnType<typeof createSchema>>;

/** Textos personalizables para i18n */
export interface LoginPageLabels {
  title?: string;
  subtitle?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  domainLabel?: string;
  domainPlaceholder?: string;
  domainRequired?: string;
  domainChecking?: string;
  domainValid?: string;
  domainNotFound?: string;
  domainNetworkError?: string;
  domainServerError?: string;
  domainVerificationError?: string;
  passwordLabel?: string;
  passwordMinLength?: string;
  submitButton?: string;
  submittingButton?: string;
  invalidCredentials?: string;
  microsoftButton?: string;
  microsoftSeparator?: string;
  microsoftInvalidDomain?: string;
  microsoftWaitValidation?: string;
  activeDomain?: string;
}

const defaultLabels: Required<LoginPageLabels> = {
  title: "FAVIT",
  subtitle: "Ingresa tus credenciales para acceder",
  emailLabel: "Email",
  emailPlaceholder: "email@empresa.com",
  domainLabel: "Dominio",
  domainPlaceholder: "empresa.com",
  domainRequired: "El dominio es obligatorio",
  domainChecking: "Verificando dominio…",
  domainValid: "Dominio encontrado.",
  domainNotFound: "No encontramos este dominio. Verifica el nombre.",
  domainNetworkError: "No se pudo conectar con el servidor.",
  domainServerError: "Error en el servidor.",
  domainVerificationError: "No se pudo verificar el dominio.",
  passwordLabel: "Contraseña",
  passwordMinLength: "La contraseña debe tener al menos 6 caracteres",
  submitButton: "Iniciar sesión",
  submittingButton: "Iniciando sesión...",
  invalidCredentials: "Credenciales inválidas",
  microsoftButton: "Entrar con Microsoft",
  microsoftSeparator: "O",
  microsoftInvalidDomain: "Por favor, ingresa un dominio válido",
  microsoftWaitValidation: "Por favor, espera a que se valide el dominio",
  activeDomain: "Dominio activo:",
};

export interface LoginPageProps {
  apiUrl: string;
  onLogin: (email: string, password: string, domain: string) => Promise<void>;
  isLoading?: boolean;
  currentDomain?: string;
  onDomainChange?: (domain: string) => void;
  tenantInfo?: LoginTenantInfo | null;
  defaultEmail?: string;
  defaultPassword?: string;
  /** URL a la que redirigir después del SSO (por defecto usa window.location.origin + '/auth/callback') */
  ssoCallbackUrl?: string;
  /** Textos personalizables para i18n */
  labels?: LoginPageLabels;
}

export function LoginPage({
  apiUrl,
  onLogin,
  isLoading = false,
  currentDomain,
  onDomainChange,
  tenantInfo,
  defaultEmail = "",
  defaultPassword = "",
  ssoCallbackUrl,
  labels: customLabels,
}: LoginPageProps) {
  const labels = { ...defaultLabels, ...customLabels };
  const schema = createSchema(labels);
  const [error, setError] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<DomainStatus>("idle");
  const [domainCheckMessage, setDomainCheckMessage] = useState<string | null>(null);
  const [hasMicrosoftSSO, setHasMicrosoftSSO] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: import.meta.env.PROD ? defaultEmail : (defaultEmail || "admin@favit.es"),
      password: import.meta.env.PROD ? defaultPassword : (defaultPassword || "Admin123!"),
      domain: currentDomain || "favit.es"
    }
  });

  const formDomainValue = watch("domain");
  const formEmailValue = watch("email");
  const lastExtractedDomainRef = useRef<string | null>(null);
  const effectiveDomain = getEffectiveDomain(
    formEmailValue,
    formDomainValue,
    currentDomain,
  );
  const apiBaseUrl = apiUrl.replace(/\/+$/, "");

  const verifyDomain = useCallback(
    async (rawDomain: string, signal?: AbortSignal) => {
      const normalized = normalizeDomain(rawDomain);
      if (!normalized) {
        return {
          status: "invalid" as DomainStatus,
          message: labels.domainRequired,
          hasMicrosoftSSO: false
        };
      }

      const { data } = await axios.get<{ exists: boolean; hasMicrosoftSSO: boolean; }>(
        `${apiBaseUrl}/auth/domains/${encodeURIComponent(normalized)}`,
        { signal }
      );

      if (data.exists) {
        return {
          status: "valid" as DomainStatus,
          hasMicrosoftSSO: data.hasMicrosoftSSO ?? false
        };
      }

      return {
        status: "invalid" as DomainStatus,
        message: labels.domainNotFound,
        hasMicrosoftSSO: false
      };
    },
    [apiBaseUrl, labels.domainRequired, labels.domainNotFound]
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      setError(null);
      const normalizedDomain = getEffectiveDomain(
        values.email,
        values.domain,
        currentDomain,
      );

      if (domainStatus !== "valid") {
        try {
          const result = await verifyDomain(normalizedDomain);
          if (result.status !== "valid") {
            setDomainStatus(result.status);
            setDomainCheckMessage(result.message ?? null);
            setHasMicrosoftSSO(false);
            return;
          }
          setDomainStatus("valid");
          setDomainCheckMessage(null);
          setHasMicrosoftSSO(result.hasMicrosoftSSO ?? false);
        } catch (domainError) {
          if (!axios.isCancel(domainError)) {
            if (axios.isAxiosError(domainError)) {
              if (domainError.code === 'ERR_NETWORK' || domainError.message.includes('Network Error')) {
                setDomainStatus("error");
                setDomainCheckMessage(labels.domainNetworkError);
              } else {
                setDomainStatus("error");
                setDomainCheckMessage(labels.domainVerificationError);
              }
            } else {
              setDomainStatus("error");
              setDomainCheckMessage(labels.domainVerificationError);
            }
            return;
          }
        }
      }

      if (onDomainChange) {
        onDomainChange(normalizedDomain);
      }

      await onLogin(values.email, values.password, normalizedDomain);
    } catch (loginError) {
      if (
        axios.isAxiosError(loginError) &&
        loginError.response?.data?.message === "Unknown tenant"
      ) {
        setDomainStatus("invalid");
        setDomainCheckMessage(labels.domainNotFound);
      }
      setError(labels.invalidCredentials);
    }
  });

  useEffect(() => {
    const normalized = effectiveDomain;

    if (!normalized) {
      setDomainStatus("idle");
      setDomainCheckMessage(null);
      setHasMicrosoftSSO(false);
      return;
    }

    const controller = new AbortController();
    setDomainStatus("checking");
    setDomainCheckMessage(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await verifyDomain(normalized, controller.signal);
        setDomainStatus(result.status);
        setDomainCheckMessage(result.message ?? null);
        setHasMicrosoftSSO(result.hasMicrosoftSSO ?? false);
      } catch (checkError) {
        if (axios.isCancel(checkError)) {
          return;
        }
        if (axios.isAxiosError(checkError)) {
          if (checkError.code === 'ERR_NETWORK' || checkError.message.includes('Network Error')) {
            setDomainStatus("error");
            setDomainCheckMessage(labels.domainNetworkError);
          } else if (checkError.response?.status === 500) {
            setDomainStatus("error");
            setDomainCheckMessage(labels.domainServerError);
          } else {
            setDomainStatus("error");
            setDomainCheckMessage(labels.domainVerificationError);
          }
        } else {
          setDomainStatus("error");
          setDomainCheckMessage(labels.domainVerificationError);
        }
      }
    }, 400);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDomain, verifyDomain]);

  useEffect(() => {
    const extractedDomain = effectiveDomain;

    if (extractedDomain !== (lastExtractedDomainRef.current ?? "")) {
      lastExtractedDomainRef.current = extractedDomain;
      setValue("domain", extractedDomain, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false
      });
      if (onDomainChange) {
        onDomainChange(extractedDomain);
      }
    }
  }, [effectiveDomain, onDomainChange, setValue]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
      {tenantInfo?.logoUrl &&
        <div className="mb-8 flex flex-col items-center">
          <img
            src={tenantInfo.logoUrl}
            alt={tenantInfo.name}
            className="h-20 w-auto max-w-[300px] object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }} />
        </div>
      }
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 shadow-surface">
        <div className="mb-8 text-center">
          {tenantInfo?.name ?
            <h1 className="text-2xl font-semibold text-content-primary">
              {tenantInfo.name}
            </h1> :
            <h1 className="text-2xl font-semibold text-content-primary">{labels.title}</h1>
          }
          <p className="mt-2 text-sm text-content-muted">
            {labels.subtitle}
          </p>
        </div>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {labels.emailLabel}
            </label>
            <Input
              placeholder={labels.emailPlaceholder}
              type="email"
              {...register("email")} />

            {errors.email &&
              <p className="mt-1 text-xs text-state-danger">
                {errors.email.message}
              </p>
            }
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {labels.domainLabel}
            </label>
            <Input
              placeholder={labels.domainPlaceholder}
              type="text"
              autoComplete="off"
              readOnly
              {...register("domain")} />

            {errors.domain &&
              <p className="mt-1 text-xs text-state-danger">
                {errors.domain.message as string}
              </p>
            }
            {!errors.domain && domainStatus === "checking" &&
              <p className="mt-1 text-xs text-content-muted">
                {labels.domainChecking}
              </p>
            }
            {!errors.domain && domainStatus === "valid" &&
              <p className="mt-1 text-xs text-state-success">
                {labels.domainValid}
              </p>
            }
            {!errors.domain && domainStatus === "invalid" &&
              <p className="mt-1 text-xs text-state-danger">
                {domainCheckMessage ?? labels.domainNotFound}
              </p>
            }
            {!errors.domain && domainStatus === "error" &&
              <p className="mt-1 text-xs text-state-warning">
                {domainCheckMessage ?? labels.domainVerificationError}
              </p>
            }
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {labels.passwordLabel}
            </label>
            <Input type="password" {...register("password")} />
            {errors.password &&
              <p className="mt-1 text-xs text-state-danger">
                {errors.password.message}
              </p>
            }
          </div>
          {error && <p className="text-sm text-state-danger">{error}</p>}
          <Button
            type="submit"
            className="w-full"
            disabled={
              isLoading ||
              domainStatus === "checking" ||
              domainStatus === "invalid"
            }>
            {isLoading ? labels.submittingButton : labels.submitButton}
          </Button>
        </form>

        {/* Separador y Botón de Microsoft - Solo si el tenant tiene SSO configurado */}
        {hasMicrosoftSSO &&
          <>
            {/* Separador */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-subtle"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-surface-primary px-2 text-xs uppercase text-content-muted">{labels.microsoftSeparator}</span>
              </div>
            </div>

            {/* Botón de Microsoft */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                const normalizedDomain = effectiveDomain;
                if (!normalizedDomain) {
                  setError(labels.microsoftInvalidDomain);
                  return;
                }
                if (domainStatus !== "valid") {
                  setError(labels.microsoftWaitValidation);
                  return;
                }
                // Usar ssoCallbackUrl si se proporciona, si no usar la URL actual + /auth/callback
                const callbackUrl = ssoCallbackUrl || `${window.location.origin}/auth/callback`;
                window.location.href = `${apiBaseUrl}/auth/microsoft?domain=${encodeURIComponent(normalizedDomain)}&redirect_uri=${encodeURIComponent(callbackUrl)}`;
              }}
              disabled={domainStatus === "checking" || domainStatus === "invalid" || domainStatus === "idle"}>
              <svg className="mr-2 h-5 w-5" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="10.5" height="10.5" fill="#F25022" />
                <rect x="12.5" y="0" width="10.5" height="10.5" fill="#7FBA00" />
                <rect x="0" y="12.5" width="10.5" height="10.5" fill="#00A4EF" />
                <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900" />
              </svg>
              {labels.microsoftButton}
            </Button>
          </>
        }
        <p className="mt-4 text-center text-xs text-content-muted">
          {labels.activeDomain} {effectiveDomain || currentDomain}
        </p>
      </div>
    </div>);
}
